import { useEffect, useState } from 'react'

function isInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
}

// Best-effort only. Reliable on Android/Chrome (shared localStorage between
// the installed PWA and browser tabs on the same origin). NOT authoritative
// on iOS — Safari/home-screen storage separation is version-dependent, and
// in-app browsers (Messages, WhatsApp, etc.) may use isolated storage jars
// entirely. This must never be the only signal driving suppression.
function hasRememberedInstall() {
  return localStorage.getItem('dp_pwa_installed') === '1'
}

type PromptMode = 'none' | 'light' | 'full'

export function InstallPrompt() {
  const [mode, setMode] = useState<PromptMode>('none')
  const [isIOS, setIsIOS] = useState(false)

  useEffect(() => {
    if (isInstalled()) {
      // Reliable signal: persist a best-effort hint for future browser-tab
      // opens (e.g. invite links) on this device.
      localStorage.setItem('dp_pwa_installed', '1')
      return
    }
    if (localStorage.getItem('dp_install_dismissed')) return
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get('preview') === 'profile') return

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
    setIsIOS(ios)
    setMode(hasRememberedInstall() ? 'light' : 'full')
  }, [])

  if (mode === 'none') return null

  const dismiss = () => {
    localStorage.setItem('dp_install_dismissed', '1')
    setMode('none')
  }

  if (mode === 'light') {
    // Compact, least-intrusive banner — no full-screen overlay, no blocking
    // of the page underneath. Purely a reminder, not an install flow.
    return (
      <div
        data-testid="banner-install-prompt-light"
        style={{
          position: 'fixed',
          left: '12px',
          right: '12px',
          bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
          zIndex: 900,
          maxWidth: '420px',
          margin: '0 auto',
          backgroundColor: '#1e3a5f',
          borderRadius: '12px',
          padding: '12px 14px',
          border: '1px solid #FACC15',
          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <p style={{
          flex: 1,
          color: '#ffffff',
          fontWeight: 600,
          fontSize: '13px',
          margin: 0,
          lineHeight: 1.4,
        }} data-testid="text-install-prompt-body">
          Already installed? Open <strong style={{ color: '#FACC15' }}>DoublePress</strong> from your Home Screen instead.
        </p>
        <button
          onClick={dismiss}
          data-testid="button-install-prompt-dismiss"
          style={{
            flexShrink: 0,
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '22px',
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    )
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#1e3a5f',
        borderRadius: '20px',
        padding: '28px',
        border: '2px solid #FACC15',
        boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        textAlign: 'center',
        position: 'relative',
      }}>
        <button
          onClick={dismiss}
          data-testid="button-install-prompt-close"
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '28px',
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <p style={{
          color: '#FACC15',
          fontWeight: 800,
          margin: '0 0 8px',
          fontSize: '24px',
          letterSpacing: '0.5px',
        }} data-testid="text-install-prompt-title">
          Install DoublePress
        </p>

        <p style={{
          color: '#ffffff',
          fontWeight: 700,
          margin: '0 0 16px',
          fontSize: '15px',
        }} data-testid="text-install-prompt-reminder">
          Already installed? Open <strong style={{ color: '#FACC15' }}>DoublePress</strong> from your Home Screen instead.
        </p>

        <div style={{
          backgroundColor: 'rgba(0,0,0,0.25)',
          borderRadius: '12px',
          padding: '18px',
          textAlign: 'left',
        }}>
          <p style={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: '13px',
            fontWeight: 700,
            margin: '0 0 10px',
          }}>
            Don't see it on your Home Screen yet?
          </p>
          {isIOS ? (
            <ol style={{
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 700,
              margin: 0,
              paddingLeft: '22px',
              lineHeight: 2.2,
            }}>
              <li>Press the <strong style={{ color: '#FACC15' }}>...</strong> on the bottom right</li>
              <li>Tap <strong style={{ color: '#FACC15' }}>"Share"</strong></li>
              <li>Scroll Down, and press <strong style={{ color: '#FACC15' }}>"Add to Home Screen"</strong></li>
            </ol>
          ) : (
            <ol style={{
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 700,
              margin: 0,
              paddingLeft: '22px',
              lineHeight: 2.2,
            }}>
              <li>Tap the <strong style={{ color: '#FACC15' }}>⋮</strong> in the top right</li>
              <li>Tap <strong style={{ color: '#FACC15' }}>"Add to Home screen"</strong></li>
              <li>Tap <strong style={{ color: '#FACC15' }}>"Add"</strong></li>
            </ol>
          )}
        </div>

        <button
          onClick={dismiss}
          data-testid="button-install-prompt-dismiss"
          style={{
            marginTop: '18px',
            width: '100%',
            backgroundColor: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '10px',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '15px',
            padding: '12px',
            cursor: 'pointer',
          }}
        >
          Got it
        </button>
      </div>
    </div>
  )
}
